import cn from 'classnames'
import {
  ELT_FORM_SECTION,
  ISoldProduct,
  ISoldProductCategory,
  ISoldProductData,
  TComplexSectionData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { memo, useCallback } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'

import { getFormedEltTranslation } from '../../../../helpers/getFormedEltTranslation'
import { Title } from '../../../styledComponents/title/Title'
import { CheckboxTranslated } from '../../checkboxTranslated/CheckboxTranslated'

import styles from './SoldProductTree.module.scss'

interface ISoldProductTreeProps {
  products: ISoldProductData
  type: string
  categories: ISoldProductCategory[]
  onChangeComplexFormData: (section: ELT_FORM_SECTION) => (data: TComplexSectionData) => void
}

export const SoldProductTree = memo<ISoldProductTreeProps>(
  ({ type, categories, products, onChangeComplexFormData }) => {
    const isAllProductsChecked = useCallback((category: ISoldProductCategory) => {
      return category.Products.every((product) => Boolean(product.Checked))
    }, [])

    const onProductCheck = useCallback(
      (category: ISoldProductCategory, product: ISoldProduct, value: boolean) => {
        const newValuer = value ? 1 : 0
        const newProduct = {
          ...product,
          Checked: newValuer,
        }
        const newCategory = {
          ...category,
          Products: category.Products.map((item) =>
            item.Name === product.Name ? newProduct : item
          ),
        }

        onChangeComplexFormData(ELT_FORM_SECTION.SOLD_PRODUCT_DATA)({
          ...products,
          [type]: products[type].map((item) => (item.Name === category.Name ? newCategory : item)),
        })
      },
      [onChangeComplexFormData, products, type]
    )

    const onCategoryCheck = useCallback(
      (category: ISoldProductCategory) => {
        const shouldCheckAll = !isAllProductsChecked(category)

        const newCategory = {
          ...category,
          Products: category.Products.map((product) => ({
            ...product,
            Checked: shouldCheckAll ? 1 : 0,
          })),
        }

        onChangeComplexFormData(ELT_FORM_SECTION.SOLD_PRODUCT_DATA)({
          ...products,
          [type]: products[type].map((item) => (item.Name === category.Name ? newCategory : item)),
        })
      },
      [isAllProductsChecked, onChangeComplexFormData, products, type]
    )

    if (categories.length === 0) {
      return null
    }

    return (
      <div className={styles.container}>
        <Title variant={'withLightBackground'} text={getFormedEltTranslation(type)} />
        <ul className={styles.productTree}>
          {categories
            .filter((category) => category?.Products?.length)
            .map((category) => (
              <li key={category.Name}>
                <div className={styles.category}>
                  <Translation text={category.Name} />
                  <Button onClick={() => onCategoryCheck(category)} variant="text">
                    {isAllProductsChecked(category) ? (
                      <Translation text="jbp-eltfrom-soldproducts-uncheck-all" />
                    ) : (
                      <Translation text="jbp-eltfrom-soldproducts-check-all" />
                    )}
                  </Button>
                </div>
                <ul>
                  {category.Products.map((product) => (
                    <div key={product.Name} className={styles.product}>
                      <li>
                        <CheckboxTranslated
                          label={product.Name}
                          checked={Boolean(product.Checked)}
                          onChange={(value) => onProductCheck(category, product, value)}
                        />
                      </li>
                      <span
                        className={cn({
                          [styles.hidden]: !product.Value,
                        })}
                      >{`${product.Value}%`}</span>
                    </div>
                  ))}
                </ul>
              </li>
            ))}
        </ul>
      </div>
    )
  }
)
